import React, { useEffect, useRef } from 'react';

const DEFAULT_CENTER = { lat: 10.1376, lng: 78.6185 };
const BBOX = '10.10,78.58,10.16,78.64';

export default function LocationPicker({ lat, lng, onChange }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markerRef = useRef(null);

  useEffect(() => {
    const loadLeaflet = async () => {
      if (!window.L) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(link);

        await new Promise((resolve) => {
          const script = document.createElement('script');
          script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
          script.onload = resolve;
          document.body.appendChild(script);
        });
      }
      initMap();
    };

    const initMap = async () => {
      const L = window.L;
      if (!mapRef.current || !L || mapInstance.current) return;
      const initialLat = lat || DEFAULT_CENTER.lat;
      const initialLng = lng || DEFAULT_CENTER.lng;
      mapInstance.current = L.map(mapRef.current).setView([initialLat, initialLng], 14);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
      }).addTo(mapInstance.current);

      if (lat && lng) {
        markerRef.current = L.marker([lat, lng]).addTo(mapInstance.current);
      }

      mapInstance.current.on('click', (e) => {
        const { lat: newLat, lng: newLng } = e.latlng;
        if (markerRef.current) {
          markerRef.current.setLatLng([newLat, newLng]);
        } else {
          markerRef.current = L.marker([newLat, newLng]).addTo(mapInstance.current);
        }
        onChange({ lat: newLat, lng: newLng });
      });

      await highlightAmenities(L);
    };

    const highlightAmenities = async (L) => {
      const query = `[out:json];node["amenity"~"hotel|supermarket|restaurant"](${BBOX});out;`;
      try {
        const response = await fetch(
          `https://overpass-api.de/api/interpreter?data=${encodeURIComponent(query)}`
        );
        const data = await response.json();
        const colors = {
          hotel: 'blue',
          supermarket: 'green',
          restaurant: 'red',
        };
        data.elements.forEach((el) => {
          if (!el.lat || !el.lon || !el.tags || !el.tags.amenity) return;
          L.circleMarker([el.lat, el.lon], {
            radius: 6,
            color: colors[el.tags.amenity] || 'gray',
            fillColor: colors[el.tags.amenity] || 'gray',
            fillOpacity: 0.8,
          })
            .addTo(mapInstance.current)
            .bindPopup(el.tags.name || el.tags.amenity);
        });
      } catch (err) {
        console.error('Amenity fetch failed', err);
      }
    };

    loadLeaflet();

    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, [onChange]);

  useEffect(() => {
    if (mapInstance.current && lat && lng) {
      mapInstance.current.setView([lat, lng], 14);
      if (markerRef.current) {
        markerRef.current.setLatLng([lat, lng]);
      } else {
        const L = window.L;
        markerRef.current = L.marker([lat, lng]).addTo(mapInstance.current);
      }
    }
  }, [lat, lng]);

  return <div ref={mapRef} style={{ height: '300px', width: '100%' }} />;
}
